import { EmptyView, emptyViewVariants } from '@components'

import { ModuleLayout } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'

import { Scope } from '../../../../enums/scope'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useIsInvoicesListEmpty } from '../InvoicesList/hooks/useIsInvoicesListEmpty'

export const InvoicesListEmpty = (): ReactElement | null => {
  const { t } = useTranslation()
  const { isInvoicesListEmpty, isLoading } = useIsInvoicesListEmpty()
  const navigate = useNavigate()

  const invoiceDocumentRouteBase = getInvoiceDocumentRouteBase(InvoiceDocumentType.Invoice)

  const { url: invoicesUrl } = useOrganizationViewUrl(reactRoute.organization.invoices.getMainRoute())
  const { url: invoiceNewUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getNewDocumentRoute(invoiceDocumentRouteBase),
  )

  const shouldSeeCreateButton = isAuthorized(Scope.InvoiceWrite)

  const handleEmptyViewClick = useCallback(() => {
    if (invoiceNewUrl) {
      navigate(invoiceNewUrl)
    }
  }, [navigate, invoiceNewUrl])

  const { animation, description, title, buttonText } = emptyViewVariants.invoices

  if (!invoicesUrl || isLoading) {
    return null
  }

  if (!isInvoicesListEmpty) {
    return <Navigate to={invoicesUrl} replace />
  }

  return (
    <ModuleLayout title={t('invoices')}>
      <EmptyView
        animationName={animation}
        onClick={handleEmptyViewClick}
        showCTA={shouldSeeCreateButton}
        tButton={buttonText}
        tDescription={description}
        tTitle={title}
      />
    </ModuleLayout>
  )
}
